import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Divider, 
  Row, 
  Col, 
  InputNumber, 
  Switch, 
  Button, 
  Typography, 
  Alert, 
  Spin, 
  Form, 
  Checkbox,
  Radio,
  Space
} from 'antd';
import api from '../../../services/api';

const TwoFactorAuth = ({ onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    enabled: false,
    required_for_admins: false,
    required_for_all: false,
    allowed_methods: ['Email'],
    remember_days: 30,
    updated_by: ''
  });

  // Fetch settings when component mounts
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await api.settings.twoFactorAuth.get();
        const settingsData = response || response?.data || {};

        const newFormData = {
          enabled: settingsData.enabled === true || settingsData.enabled === 'true',
          required_for_admins: settingsData.required_for_admins === true || settingsData.required_for_admins === 'true',
          required_for_all: settingsData.required_for_all === true || settingsData.required_for_all === 'true',
          allowed_methods: Array.isArray(settingsData.allowed_methods) 
            ? settingsData.allowed_methods 
            : (settingsData.allowed_methods ? [settingsData.allowed_methods] : ['Email']),
          remember_days: Number(settingsData.remember_days) || 30,
          updated_by: settingsData.updated_by || ''
        };
        
        setFormData(newFormData);
        
        // if (Object.keys(settingsData).length > 0) {
        //   // Check if the response has the two_factor_auth key (from get_all_security_settings)
        //   // or if it's the direct 2FA settings response (from get_two_factor_auth_settings)
        //   const twoFactorSettings = settingsData.enabled || settingsData;
          
        //   console.log("twoFactorSettings:", twoFactorSettings);
        //   const newFormData = {
        //     enabled: settingsData.enabled === true || settingsData.enabled === 'true',
        //     required_for_admins: settingsData.required_for_admins === true || settingsData.required_for_admins === 'true',
        //     required_for_all: settingsData.required_for_all === true || settingsData.required_for_all === 'true',
        //     allowed_methods: Array.isArray(settingsData.allowed_methods) 
        //       ? settingsData.allowed_methods 
        //       : (settingsData.allowed_methods ? [settingsData.allowed_methods] : ['Email']),
        //     remember_days: Number(settingsData.remember_days) || 30,
        //     updated_by: settingsData.updated_by || ''
        //   };
          
        //   console.log("New form data to be set:", newFormData);
        //   setFormData(newFormData);
        // }
      } catch (error) {
        console.error('Error fetching 2FA settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Ensure we always get a boolean for switch/checkbox inputs
    const newValue = type === 'checkbox' || type === 'switch' ? Boolean(checked) : value;
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: newValue
      };
      
      // If enabling 2FA, ensure default values are set
      if (name === 'enabled' && newValue === true) {
        updatedData.allowed_methods = Array.isArray(prev.allowed_methods) && prev.allowed_methods.length 
          ? prev.allowed_methods 
          : ['Email'];
        updatedData.remember_days = prev.remember_days || 30;
      }
      
      return updatedData;
    });
  };
  
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : parseInt(value, 10)
      }));
    }
  };
  
  const handleMethodChange = (method) => (event) => {
    const newMethods = formData.allowed_methods.includes(method)
      ? formData.allowed_methods.filter(m => m !== method)
      : [...formData.allowed_methods, method];
    
    setFormData(prev => ({
      ...prev,
      allowed_methods: newMethods
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (formData.enabled && formData.allowed_methods.length === 0) {
      newErrors.allowed_methods = 'At least one authentication method must be selected';
    }
    
    if (formData.remember_days < 1 || formData.remember_days > 90) {
      newErrors.remember_days = 'Remember days must be between 1 and 90';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    
    try {
      await onUpdate(formData);
    } finally {
      setSaving(false);
    }
  };
  
  if (loading && !formData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }
  
  return (
    <Card
      title="Two-Factor Authentication"
      extra={
        <Typography.Text type="secondary">
          Configure two-factor authentication settings
        </Typography.Text>
      }
    >
      <Divider />
      <div style={{ padding: '24px' }}>
        <Form layout="vertical" onFinish={handleSubmit}>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <Space>
                <Form.Item noStyle >
                  <Switch 
                    checked={Boolean(formData.enabled)}
                    onChange={(checked) => {
                      const newValue = !formData.enabled;
                      setFormData(prev => ({
                        ...prev,
                        enabled: newValue
                      }));
                    }}
                    checkedChildren="On" 
                    unCheckedChildren="Off"
                  />
                </Form.Item>
                <span style={{ marginLeft: 8 }}>Enable Two-Factor Authentication</span>
              </Space>
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                Require users to verify their identity using a second factor in addition to their password.
              </Typography.Text>
            </Col>
           
            {formData.enabled && (
              <>
                <Col xs={24} md={12}>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography.Title level={5} style={{ marginBottom: '16px' }}>
                      Requirements
                    </Typography.Title>
                    
                    <Form.Item>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Form.Item noStyle>
                          <Space>
                            <Form.Item noStyle>
                              <Switch 
                                checked={Boolean(formData.required_for_admins)}
                                onChange={(checked) => {
                                  const newValue = !formData.required_for_admins;
                                  setFormData(prev => ({
                                    ...prev,
                                    required_for_admins: newValue
                                  }));
                                }}
                                checkedChildren="On" 
                                unCheckedChildren="Off"
                              />
                            </Form.Item>
                            <span>Require for all admin accounts</span>
                          </Space>
                        </Form.Item>
                        
                        <Form.Item noStyle>
                          <Space>
                            <Form.Item noStyle>
                              <Switch 
                                checked={Boolean(formData.required_for_all)}
                                onChange={() => {
                                  const newValue = !formData.required_for_all;
                                  setFormData(prev => ({
                                    ...prev,
                                    required_for_all: newValue
                                  }));
                                }}
                                checkedChildren="On" 
                                unCheckedChildren="Off"
                              />
                            </Form.Item>
                            <span>Require for all user accounts</span>
                          </Space>
                        </Form.Item>
                      </Space>
                    </Form.Item>
                    
                    <div style={{ marginTop: '24px' }}>
                      <Typography.Title level={5} style={{ marginBottom: '16px' }}>
                        Trusted Devices
                      </Typography.Title>
                      
                      <Form.Item
                        label="Remember Trusted Devices (Days)"
                        validateStatus={errors.remember_days ? 'error' : ''}
                        help={errors.remember_days || 'Number of days to remember trusted devices (1-90)'}
                      >
                        <InputNumber
                          min={1}
                          max={90}
                          value={formData.remember_days}
                          onChange={(value) => handleNumberChange({ target: { name: 'remember_days', value } })}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </Col>
                
                <Col xs={24} md={12}>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography.Title level={5} style={{ marginBottom: '16px' }}>
                      Authentication Methods
                    </Typography.Title>
                    
                    <Form.Item>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {['Email'].map((method) => (
                          <Form.Item noStyle key={method}>
                            <Checkbox
                              checked={formData.allowed_methods.includes(method)}
                              onChange={() => handleMethodChange(method)({ target: { checked: !formData.allowed_methods.includes(method) } })}
                            >
                              {method === 'Email'? 'Email': '---'}
                            </Checkbox>
                          </Form.Item>
                        ))}
                        
                        {errors.allowed_methods && (
                          <Typography.Text type="danger">
                            {errors.allowed_methods}
                          </Typography.Text>
                        )}
                      </Space>
                    </Form.Item>
                    
                    <div style={{ marginTop: '24px' }}>
                      <Typography.Title level={5} style={{ marginBottom: '16px' }}>
                        Default Method
                      </Typography.Title>
                      
                      <Form.Item
                        label="Default 2FA Method"
                        help="Select the default 2FA method for new users."
                      >
                        <Radio.Group 
                          value={formData.default_method || ''}
                          onChange={(e) => handleChange({ target: { name: 'default_method', value: e.target.value } })}
                          disabled={!formData.allowed_methods.length}
                        >
                          <Space direction="vertical">
                            {formData.allowed_methods.map(method => (
                              <Radio key={method} value={method}>
                                {method === 'Email' ? 'Email' : method}
                              </Radio>
                            ))}
                          </Space>
                        </Radio.Group>
                      </Form.Item>
                    </div>
                  </div>
                </Col>
              </>
            )}
            
            <Col span={24}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  disabled={formData.enabled && formData.allowed_methods.length === 0}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </Col>
            
            {Object.keys(errors).length > 0 && (
              <Col span={24}>
                <Alert
                  message="Please fix the following errors:"
                  description={
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {Object.values(errors).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  }
                  type="error"
                  showIcon
                />
              </Col>
            )}
          </Row>
        </Form>
      </div>
    </Card>
  );
};

export default TwoFactorAuth;
